import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  ChevronRight,
  ChevronDown,
  Crown,
  Sparkles,
  Check,
  X,
  Sun,
  Moon,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import {
  SKIN_ROUTINES,
  HAIR_ROUTINES,
  DEFAULT_SKIN,
  DEFAULT_HAIR,
} from "../data/routines";
import { enrichSteps } from "../data/step-meta";
import { RECIPES } from "../data/recipes";
import { cleanConcerns } from "../data/onboarding";
import { useUpgradeModal } from "../components/AppShell";
import ProductChips from "../components/ProductChips";
import { supabase } from "../lib/supabase";
import { toast } from "sonner";

const QUICK_LIMIT = 3;
const TIERS = ["All", "Budget", "Mid-range", "Luxury"];

export default function Routines() {
  const { profile, refreshProfile } = useAuth();
  const upgrade = useUpgradeModal();
  const navigate = useNavigate();
  const location = useLocation();

  const params = new URLSearchParams(location.search);
  const initialTime = params.get("time") === "PM" ? "PM" : "AM";

  const [tab, setTab] = useState("skin"); // skin | hair
  const [time, setTime] = useState(initialTime);
  const [mode, setMode] = useState("full");
  const [guided, setGuided] = useState(null); // null | { steps, index }

  const skinKey = profile?.skin_type || DEFAULT_SKIN;
  const hairKey = profile?.hair_type || DEFAULT_HAIR;
  // Concerns are stored with skin:/hair: prefixes in the DB — strip them
  // before passing to product matchers, which compare against raw label
  // strings in products.concerns[].
  const concerns = useMemo(() => cleanConcerns(profile?.concerns || []), [profile?.concerns]);
  const isPremium = !!profile?.is_premium;

  const skinRoutine = SKIN_ROUTINES[skinKey] || SKIN_ROUTINES[DEFAULT_SKIN];
  const hairRoutine = HAIR_ROUTINES[hairKey] || HAIR_ROUTINES[DEFAULT_HAIR];
  const rawSteps = useMemo(
    () =>
      tab === "skin"
        ? skinRoutine[time] || []
        : hairRoutine[time] || hairRoutine.AM || [],
    [tab, time, skinRoutine, hairRoutine]
  );

  const enriched = useMemo(
    () => enrichSteps(rawSteps, time, tab, concerns),
    [rawSteps, time, tab, concerns]
  );
  const visibleSteps = mode === "quick" ? enriched.slice(0, QUICK_LIMIT) : enriched;

  const headerTitle = tab === "skin" ? "Your Skincare Routine" : "Your Hair Routine";
  const startCta =
    time === "AM"
      ? `Start Morning Routine`
      : `Start Evening Routine`;

  const startGuided = async () => {
    // Free users can preview first 3 steps. Premium gets all.
    const stepsForRun = isPremium ? enriched : enriched.slice(0, QUICK_LIMIT);
    setGuided({ steps: stepsForRun, index: 0 });
  };

  const finishGuided = async () => {
    setGuided(null);
    if (!profile?.id) return;
    const today = new Date().toISOString().slice(0, 10);
    await supabase.from("progress_logs").insert({
      user_id: profile.id,
      log_date: today,
      type: "routine",
      mood: "✨",
      note: `Completed ${tab} ${time} routine`,
    });
    await supabase
      .from("users")
      .update({ streak: (profile.streak || 0) + 1 })
      .eq("id", profile.id);
    await refreshProfile();
    toast.success("Routine complete — streak updated ✨");
  };

  return (
    <div className="-mt-px relative" data-testid="routines-page">
      {/* PINK HEADER */}
      <div
        className="relative pt-5 px-5 pb-5"
        style={{ background: "linear-gradient(180deg, #C9748A 0%, #B8657A 100%)" }}
        data-testid="routines-header"
      >
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/app")}
            data-testid="routines-back"
            className="w-9 h-9 rounded-full bg-white/15 backdrop-blur-sm text-white flex items-center justify-center"
          >
            <ArrowLeft size={18} />
          </button>
          <h1 className="font-serif text-xl text-white" data-testid="routines-title">
            {headerTitle}
          </h1>
        </div>
      </div>

      {/* CONTENT */}
      <div className="px-5 pt-5 pb-44">
        <h2 className="font-serif text-3xl mb-4" style={{ color: "#2E1608" }}>
          Steps for today
        </h2>

        {/* Skin profile card */}
        <Link
          to="/app/profile"
          data-testid="profile-card"
          className="block rounded-2xl p-4 mb-4"
          style={{ background: "#FDE8F0" }}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-rose mb-0.5">
                Your {tab === "skin" ? "skin" : "hair"} profile
              </p>
              <p className="text-[11px] font-light text-rose/80">
                Routine based on: {tab === "skin" ? skinKey : hairKey}
                {tab === "skin" && profile?.skin_tone ? ` · ${profile.skin_tone}` : ""}
              </p>
            </div>
            <ChevronRight size={18} className="text-rose" />
          </div>
        </Link>

        {/* Weekly adaptive routine — PREMIUM */}
        {isPremium && <AdaptiveRoutineBanner userId={profile?.id} tab={tab} skinType={skinKey} hairType={hairKey} concerns={concerns} />}

        {/* Skin / Hair tabs */}
        <div className="flex gap-2 mb-3">
          <TopTab
            label="Skin"
            active={tab === "skin"}
            onClick={() => setTab("skin")}
            testId="routines-tab-skin"
          />
          <TopTab
            label="Hair"
            active={tab === "hair"}
            onClick={() => setTab("hair")}
            testId="routines-tab-hair"
          />
        </div>

        {/* Time toggle + Quick/Full */}
        <div className="flex items-center gap-2 mb-5">
          <div
            className="flex p-1 rounded-full flex-1"
            style={{ background: "#f0e8e4" }}
          >
            <TimePill
              active={time === "AM"}
              onClick={() => setTime("AM")}
              icon={<Sun size={14} />}
              label="Morning"
              testId="time-am"
            />
            <TimePill
              active={time === "PM"}
              onClick={() => setTime("PM")}
              icon={<Moon size={14} />}
              label="Evening"
              testId="time-pm"
            />
          </div>
          <div className="flex p-1 rounded-full" style={{ background: "#f0e8e4" }}>
            {["quick", "full"].map((m) => (
              <button
                key={m}
                onClick={() => setMode(m)}
                data-testid={`mode-${m}`}
                className={`px-3 py-1 rounded-full text-[10px] uppercase tracking-wider font-medium transition-all ${
                  mode === m ? "bg-cocoa text-white" : "text-cocoa/55"
                }`}
              >
                {m}
              </button>
            ))}
          </div>
        </div>

        {/* Step cards */}
        <div className="space-y-4">
          {visibleSteps.map((step, i) => {
            const locked = !isPremium && i >= QUICK_LIMIT && mode === "full";
            return (
              <div key={i}>
                {/* Outside number + category */}
                <div className="flex items-center gap-2 mb-2 px-1">
                  <div
                    className="w-6 h-6 rounded-full bg-rose text-white flex items-center justify-center text-[11px] font-medium"
                    data-testid={`step-num-${i}`}
                  >
                    {i + 1}
                  </div>
                  <span className="text-[11px] uppercase tracking-[0.2em] text-rose font-medium">
                    {step.category}
                  </span>
                </div>
                <div className="relative">
                  <StepCard step={step} index={i} blurred={locked} />
                  {locked && i === QUICK_LIMIT && (
                    <PremiumOverlay onUpgrade={() => upgrade.open("Guided routine")} />
                  )}
                </div>
                {!locked && (
                  <ProductChips
                    text={`${step.type} ${step.name} ${step.instruction || ""}`}
                    limit={1}
                    testIdPrefix={`step-chip-${i}`}
                    mode={tab === "hair" ? "hair" : "skin"}
                    skinType={skinKey}
                    hairType={hairKey}
                    concerns={concerns}
                  />
                )}
              </div>
            );
          })}
        </div>

        {/* Recommended DIY Routine */}
        <RecommendedDIY tab={tab} />

        {/* Wash Day card (hair only) */}
        {tab === "hair" && (
          <Link
            to="/app/routines?time=PM"
            data-testid="wash-day-card"
            className="block rounded-2xl p-4 mt-5 border"
            style={{ background: "#FDF5E8", borderColor: "#E3BA85" }}
          >
            <p className="text-[10px] uppercase tracking-wider mb-1" style={{ color: "#C9975A" }}>
              Wash day ritual
            </p>
            <h3 className="font-serif text-xl mb-1" style={{ color: "#2E1608" }}>
              Sunday wash day routine
            </h3>
            <p className="text-xs text-cocoa/65 font-light">
              Pre-poo, cleanse, deep condition, LOC method — your complete weekly wash-day flow.
            </p>
          </Link>
        )}

        {/* Dupe Finder moved to /app/dupes — accessible via bottom nav */}
      </div>

      {/* Sticky start button */}
      <div
        className="fixed left-0 right-0 bottom-[80px] z-20 px-5 py-3 pointer-events-none"
        data-testid="start-cta-wrap"
      >
        <div className="max-w-[430px] mx-auto pointer-events-auto">
          <button
            onClick={startGuided}
            data-testid="start-routine-btn"
            className="w-full rounded-full bg-rose hover:bg-rose-dark text-white py-3.5 font-medium text-sm transition-all shadow-rose flex items-center justify-center gap-2"
          >
            {startCta}
            <ArrowRight size={16} />
          </button>
        </div>
      </div>

      {/* Guided mode fullscreen */}
      <AnimatePresence>
        {guided && (
          <GuidedMode
            session={guided}
            onClose={() => setGuided(null)}
            onAdvance={(nextIdx) =>
              setGuided((g) => ({ ...g, index: nextIdx }))
            }
            onFinish={finishGuided}
            time={time}
            tab={tab}
            skinType={skinKey}
            hairType={hairKey}
            concerns={concerns}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

function TopTab({ label, active, onClick, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
        active
          ? "bg-rose text-white shadow-rose"
          : "bg-white text-cocoa/60 border border-[#f0e8e4]"
      }`}
    >
      {label}
    </button>
  );
}

function TimePill({ active, onClick, icon, label, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`flex-1 flex items-center justify-center gap-1.5 rounded-full text-xs font-medium py-1.5 transition-all ${
        active ? "bg-rose text-white shadow-rose" : "text-cocoa/55"
      }`}
    >
      {icon}
      {label}
    </button>
  );
}

function StepCard({ step, index, blurred }) {
  return (
    <div
      className={`bg-white rounded-2xl p-4 border border-[#f0e8e4] flex items-start gap-3 ${
        blurred ? "blur-sm select-none" : ""
      }`}
      data-testid={`step-card-${index}`}
    >
      <div
        className="w-[52px] h-[52px] rounded-xl flex items-center justify-center text-2xl shrink-0"
        style={{ background: step.bg }}
      >
        {step.emoji}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[10px] uppercase tracking-wider text-rose font-medium mb-0.5">
          {step.type}
        </p>
        <h3 className="font-serif text-[17px] leading-tight mb-1" style={{ color: "#2E1608" }}>
          {step.name}
        </h3>
        <p className="text-[11px] text-cocoa/60 font-light leading-snug">
          {step.instruction}
        </p>
      </div>
      <div className="flex flex-col items-end gap-1 shrink-0">
        {step.isMatch && (
          <span className="inline-flex items-center gap-1 text-[10px] font-medium text-green-700 bg-green-50 px-2 py-1 rounded-full whitespace-nowrap">
            <Check size={10} /> Match
          </span>
        )}
        {step.timeLabel && (
          <span className="text-[10px] font-medium px-2 py-1 rounded-full whitespace-nowrap" style={{ background: "#FDF5E8", color: "#C9975A" }}>
            {step.timeLabel}
          </span>
        )}
      </div>
    </div>
  );
}

function PremiumOverlay({ onUpgrade }) {
  return (
    <div
      className="absolute inset-0 -inset-x-1 flex flex-col items-center justify-center text-center rounded-2xl backdrop-blur-md bg-white/55 p-4"
      data-testid="guided-premium-overlay"
    >
      <Crown size={22} className="mb-1" style={{ color: "#C9975A" }} />
      <p className="text-[11px] uppercase tracking-[0.18em] font-medium mb-0.5" style={{ color: "#C9975A" }}>
        Guided Mode — Premium
      </p>
      <p className="text-xs text-cocoa/65 font-light mb-3">
        Start guided step-by-step ritual
      </p>
      <button
        onClick={onUpgrade}
        data-testid="unlock-guided-btn"
        className="rounded-full px-5 py-2 text-xs text-white font-medium shadow-rose"
        style={{ background: "#C9748A" }}
      >
        Unlock Premium
      </button>
    </div>
  );
}

function AdaptiveRoutineBanner({ userId, tab, skinType, hairType, concerns }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!userId) return;
    supabase
      .from("adaptive_routines")
      .select("payload, week_start, season, generated_at")
      .eq("user_id", userId)
      .order("week_start", { ascending: false })
      .limit(1)
      .maybeSingle()
      .then(({ data }) => setData(data));
  }, [userId]);

  if (!data) return null;
  const p = data.payload || {};
  const steps =
    tab === "skin"
      ? [...(p.skin?.AM || []), ...(p.skin?.PM || [])]
      : [...(p.hair?.daily || []), ...(p.hair?.wash_day || [])];

  return (
    <div
      className="rounded-2xl p-4 mb-4 text-white relative overflow-hidden"
      style={{ background: "linear-gradient(135deg, #C9975A 0%, #E3BA85 100%)" }}
      data-testid="adaptive-routine-banner"
    >
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full text-left flex items-start gap-3"
        data-testid="adaptive-toggle"
      >
        <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center shrink-0">
          <Crown size={16} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-[0.18em] opacity-85 mb-0.5">
            This week's evolved ritual · {data.season}
          </p>
          <h3 className="font-serif text-base leading-tight">{p.headline}</h3>
        </div>
        <ChevronDown
          size={18}
          className="shrink-0 transition-transform"
          style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)" }}
        />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            <p className="text-xs font-light leading-relaxed mt-3 opacity-95">
              {p.rationale}
            </p>
            {steps.length > 0 && (
              <div className="mt-3 space-y-1.5">
                {steps.slice(0, 6).map((s, i) => (
                  <div
                    key={i}
                    className="text-[11px] bg-white/15 rounded-lg px-2.5 py-1.5 font-light"
                  >
                    {i + 1}. {s}
                  </div>
                ))}
              </div>
            )}
            {(p.focus_ingredients || []).length > 0 && (
              <p className="text-[10px] mt-3 opacity-85">
                <strong className="font-medium">Focus:</strong>{" "}
                {(p.focus_ingredients || []).join(", ")}
              </p>
            )}
            {(p.avoid_this_week || []).length > 0 && (
              <p className="text-[10px] mt-1 opacity-85">
                <strong className="font-medium">Avoid:</strong>{" "}
                {(p.avoid_this_week || []).join(", ")}
              </p>
            )}
            {/* Product chips matched to the focus ingredients + step text. */}
            <div className="mt-3 -mx-1">
              <ProductChips
                text={[
                  ...(p.focus_ingredients || []),
                  ...steps,
                ].join(" ")}
                limit={3}
                testIdPrefix="adaptive-chip"
                mode={tab === "hair" ? "hair" : "skin"}
                skinType={tab === "skin" ? skinType : null}
                hairType={tab === "hair" ? hairType : null}
                concerns={concerns}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function RecommendedDIY({ tab }) {
  const recipe = useMemo(() => {
    return (
      RECIPES.find((r) => r.type === tab && !r.premium) ||
      RECIPES.find((r) => !r.premium)
    );
  }, [tab]);
  if (!recipe) return null;
  return (
    <Link
      to="/app/diy"
      data-testid="diy-reco"
      className="block bg-white rounded-2xl p-4 mt-5 border border-[#f0e8e4]"
    >
      <div className="flex items-center gap-3">
        <div
          className="w-14 h-14 rounded-xl flex items-center justify-center text-2xl shrink-0"
          style={{ background: "#FDF5E8" }}
        >
          🥥
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-wider text-rose font-medium mb-0.5">
            Recommended DIY Routine
          </p>
          <h3 className="font-serif text-base leading-tight mb-0.5" style={{ color: "#2E1608" }}>
            {recipe.name}
          </h3>
          <p className="text-[11px] text-cocoa/55 font-light">
            {recipe.prepTime} · {recipe.note}
          </p>
        </div>
        <ChevronRight size={18} className="text-cocoa/40" />
      </div>
    </Link>
  );
}

/* ===== Fullscreen guided ritual ===== */

function GuidedMode({ session, onClose, onAdvance, onFinish, time, tab, skinType, hairType, concerns }) {
  const { steps, index } = session;
  const step = steps[index];
  const isLast = index >= steps.length - 1;
  const progress = ((index + 1) / steps.length) * 100;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-cream flex flex-col"
      style={{ zIndex: 100001 }}
      data-testid="guided-fullscreen"
    >
      {/* Top bar */}
      <div className="px-5 pt-5 pb-3">
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs uppercase tracking-wider text-cocoa/55 font-medium">
            {tab === "skin" ? "Skincare" : "Haircare"} · {time === "AM" ? "Morning" : "Evening"}
          </span>
          <button onClick={onClose} data-testid="guided-close" className="text-cocoa/60">
            <X size={20} />
          </button>
        </div>
        <div className="h-1 rounded-full bg-cocoa/10 overflow-hidden">
          <motion.div
            className="h-full bg-rose"
            initial={false}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.3 }}
          />
        </div>
        <p className="text-[10px] text-cocoa/55 mt-1.5">
          Step {index + 1} of {steps.length}
        </p>
      </div>

      {/* Step */}
      <motion.div
        key={index}
        initial={{ opacity: 0, x: 12 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -12 }}
        className="flex-1 flex flex-col items-center justify-center px-8 text-center"
      >
        <div
          className="w-32 h-32 rounded-3xl flex items-center justify-center text-7xl mb-6"
          style={{ background: step.bg }}
        >
          {step.emoji}
        </div>
        <p className="text-[11px] uppercase tracking-[0.2em] text-rose font-medium mb-2">
          {step.category}
        </p>
        <h2 className="font-serif text-3xl mb-4 leading-tight" style={{ color: "#2E1608" }}>
          {step.name}
        </h2>
        <p className="text-base text-cocoa/70 font-light leading-relaxed max-w-xs">
          {step.instruction}
        </p>
        <div className="w-full max-w-xs mt-4">
          <ProductChips
            text={`${step.type || ""} ${step.name} ${step.instruction || ""}`}
            limit={1}
            testIdPrefix="guided-chip"
            mode={tab === "hair" ? "hair" : "skin"}
            skinType={tab === "skin" ? skinType : null}
            hairType={tab === "hair" ? hairType : null}
            concerns={concerns}
          />
        </div>
      </motion.div>

      {/* Bottom CTA — pb-safe keeps button above device safe area */}
      <div className="p-5 pb-safe">
        <button
          onClick={() => (isLast ? onFinish() : onAdvance(index + 1))}
          data-testid="guided-next"
          className="w-full rounded-full bg-rose hover:bg-rose-dark text-white py-4 font-medium text-sm transition-all shadow-rose flex items-center justify-center gap-2"
        >
          {isLast ? (
            <>
              Finish ritual <Sparkles size={16} />
            </>
          ) : (
            <>
              Done — next step <ArrowRight size={16} />
            </>
          )}
        </button>
      </div>
    </motion.div>
  );
}
